<template>
  <section v-show="fragmentsLoaded" class="eptp_content">
    <div class="container">
      <ul class="breadcrumbs">
        <li>
          <a :href="homeHref">{{ $t('pages.home.title') }}</a>
        </li>
        <li>
          <a :href="$url($t('pages.services.url'))">{{ $t('pages.services.title') }}</a>
        </li>
      </ul>

      <BaseGlobalAlert />

      <h1>{{ $t('pages.services.title') }}</h1>

      <form class="form-inline" @submit.prevent="getServices(1)">
        <label class="filter_form__label" for="txtSearch">
          {{ $t('common.search') }}
        </label>
        <div class="input-group service-search">
          <input
            v-model="serviceParams.query"
            :placeholder="$t('common.searchPlaceholder')"
            type="text"
            class="form-control"
            id="txtSearch"
          />
          <button :disabled="loadingServices" class="btn btn-regular" :title="$t('common.search')" type="submit">
            <i class="icon-search"></i>
          </button>
        </div>
      </form>

      <div class="clearfix">
        <div class="c-side-content">
          <h3>
            {{ $t('pages.services.filters.group') }}
          </h3>
          <div class="c-checkbox-group">
            <div v-for="group in serviceGroups" :key="group.code">
              <input
                v-model="serviceParams.group"
                :id="`group-${group.code}`"
                :value="group.code"
                type="checkbox"
                @change="getServices(1)"
              />
              <label :for="`group-${group.code}`">
                {{ group.name }}
              </label>
            </div>
          </div>
          <h3>
            {{ $t('pages.services.filters.provider') }}
          </h3>
          <div class="c-checkbox-group">
            <div
              v-for="provider in serviceProviders.slice(0, visibleProviderCount)"
              :key="provider.code"
            >
              <input
                v-model="serviceParams.provider"
                :id="`provider-${provider.code}`"
                :value="provider.code"
                type="checkbox"
                @change="getServices(1)"
              />
              <label :for="`provider-${provider.code}`">
                {{ provider.name }}
              </label>
            </div>
            <div v-show="showAllProviders">
              <div
                v-for="provider in serviceProviders.slice(visibleProviderCount)"
                :key="provider.code"
              >
                <input
                  v-model="serviceParams.provider"
                  :id="`provider-${provider.code}`"
                  :value="provider.code"
                  type="checkbox"
                  @change="getServices(1)"
                />
                <label :for="`provider-${provider.code}`">
                  {{ provider.name }}
                </label>
              </div>
            </div>
            <a
              v-if="serviceProviders.length > visibleProviderCount"
              href="#"
              @click.prevent="showAllProviders = !showAllProviders"
              >{{
                $t(`pages.services.actions.show${showAllProviders ? 'Less' : 'More'}Providers`)
              }}</a
            >
          </div>
          <h3>
            {{ $t('pages.services.filters.paymentInfo') }}
          </h3>
          <div class="c-checkbox-group">
            <div>
              <input
                v-model="serviceParams.isChargeable"
                :value="true"
                type="checkbox"
                id="chargeable-service"
                @change="getServices(1)"
              />
              <label for="chargeable-service">
                {{ $t('pages.services.filters.chargeableService') }}
              </label>
            </div>
            <div>
              <input
                v-model="serviceParams.isChargeable"
                :value="false"
                type="checkbox"
                id="free-service"
                @change="getServices(1)"
              />
              <label for="free-service">
                {{ $t('pages.services.filters.freeService') }}
              </label>
            </div>
          </div>
        </div>
        <div class="c-main-content">
          <div class="c-service-list relative">
            <div v-if="loadingServices" class="loading-overlay"></div>
            <div v-if="loadingServices && !services.length">{{ $t('common.loading') }}...</div>
            <div v-if="!loadingServices && !services.length">
              {{ $t('common.noData') }}
            </div>
            <div v-for="service in services" :key="service.id" class="c-service-item">
              <div class="c-service-item__title">
                {{ service.name }}
              </div>
              <div v-if="service.description" class="c-service-item__content">
                <div class="c-service-item__description" style="white-space: pre-line;">
                  <i class="glyphicon glyphicon-info-sign"></i> {{ service.description }}
                </div>
                <div v-if="service.provider" class="c-service-item__provider">
                  <i class="glyphicon glyphicon-user"></i>
                  {{ service.provider.name }}
                </div>
                <div class="c-service-item__controls">
                  <button class="btn btn-feat" @click="viewService(service)">
                    {{ $t('pages.services.actions.order') }}
                  </button>
                  <span v-if="service.isChargeable" class="h-color--red">{{
                    $t('pages.services.labels.chargeableService')
                  }}</span>
                  <span v-else>{{ $t('pages.services.labels.freeService') }}</span>
                </div>
              </div>
            </div>
          </div>
          <BasePagination
            :current-page="servicePagination.page"
            :page-size="servicePagination.pageSize"
            :total-items="servicePagination.total"
            @update:page="getServices"
          />
        </div>
      </div>
    </div>

    <ImportantMessagesModal
      :loading="loadingImportantMessage"
      :message="displayedImportantMessage"
      @read="markImportantMessageAsRead"
    />
  </section>
</template>

<script>
import queryString from 'query-string';
import BasePagination from '../../components/base/BasePagination';
import pageMixin from '../../mixins/page.mixin';
import classifierService from '../../services/classifier.service';
import serviceDirectoryService from '../../services/serviceDirectory.service';
import BaseGlobalAlert from '../../components/base/BaseGlobalAlert';
import { updateUrl } from '../../util/url';

export default {
  name: 'Services',

  components: { BaseGlobalAlert, BasePagination },

  mixins: [pageMixin],

  data() {
    return {
      loadingServices: false,
      services: [],
      servicePagination: {},
      serviceGroups: [],
      serviceProviders: [],
      serviceParams: {
        provider: [],
        group: [],
        isChargeable: [],
      },
      showAllProviders: false,
      visibleProviderCount: 4, // if there are more providers than this they will be collapsed by default
    };
  },

  created() {
    document.title = `EPTP - ${this.$t('pages.services.title')}`;
    this.serviceParams = this.getServiceParamsFromQuery(window.location.search);
    this.getServices(this.serviceParams.page);
    classifierService.getServiceGroups().then((res) => {
      this.serviceGroups = res.data.serviceGroup;
    });
    classifierService.getServiceProviders().then((res) => {
      this.serviceProviders = res.data.serviceProvider;
    });
  },

  mounted() {
    this.setupPage();
    window.addEventListener('popstate', this.onPopState);
  },

  beforeDestroy() {
    window.removeEventListener('popstate', this.onPopState);
  },

  methods: {
    getServiceParamsFromQuery(query) {
      const urlParams = queryString.parse(query);
      const params = {
        ...urlParams,
        group: urlParams.group || [],
        provider: urlParams.provider || [],
        isChargeable: urlParams.isChargeable || [],
      };
      if (typeof params?.group === 'string') {
        // when query param has just one group in it, it decodes it into a string instead of array
        params.group = [params.group];
      }
      if (typeof params?.provider === 'string') {
        params.provider = [params.provider];
      }
      if (typeof params?.isChargeable === 'string') {
        params.isChargeable = [params.isChargeable];
      }
      return params;
    },

    getServices(page = 1) {
      this.loadingServices = true;
      const params = { ...this.serviceParams };
      if (+page > 1) {
        params.page = page;
      } else {
        delete params.page;
      }
      updateUrl(this.serviceParams);
      if (params.isChargeable?.length === 1) {
        // isChargeable filter value is a boolean, but there are two checkboxes that control it
        // marking both is the same as marking none
        [params.isChargeable] = params.isChargeable;
      } else {
        delete params.isChargeable;
      }
      serviceDirectoryService
        .getList(params)
        .then((res) => {
          this.services = res.data.service;
          this.servicePagination = {
            page: res.data.page,
            total: res.data.total,
            pageSize: res.data.pageSize,
          };
        })
        .catch(() => {
          this.services = [];
          this.servicePagination = {};
        })
        .finally(() => {
          this.loadingServices = false;
        });
    },

    onPopState() {
      this.serviceParams = this.getServiceParamsFromQuery(window.location.search);
      this.getServices();
    },

    viewService(service) {
      window.location.href = this.$url(`${this.$t('pages.service.url')}?id=${service.id}`);
    },
  },
};
</script>

<style>
@import '../../../assets/css/style.min.css';
@import '../../assets/index.css';
</style>
